<template>
    <div id="agent-chat">
      <!-- Header -->
      <div class="chat-header">
        <div class="header-left">
          <h2>
            <span
              class="status-indicator"
              :class="{ 'status-thinking': isThinking }"
            ></span>
            {{ agentName }}
          </h2>
          <p v-if="claimNumber" class="claim-context">
            Claim #{{ claimNumber }}
          </p>
        </div>
        <beta-chip color="#0092AB" />
        <div class="header-actions">
          <el-button
            type="text"
            @click="confirmClearConversation"
            :disabled="isThinking || messages.length === 0"
            title="Clear conversation and start new session"
            class="clear-button"
          >
            New Chat
          </el-button>
          <span
            v-if="!isMobile"
            @click="onAgentChatClose"
            class="action-icon icon-chevron-down icon-chevron-right chevron-icon">
          </span>
          <span 
            v-if="isMobile" 
            class="action-icon icon-close" 
            @click="onAgentChatClose">
          </span>
        </div>
      </div>
  
      <!-- Messages Area -->
      <div ref="messagesContainer" class="messages-container">
        <!-- Welcome message when empty -->
        <div v-if="messages.length === 0" class="welcome-message">
          <div class="welcome-icon agent-icon">
            <img :src="agentIconPath" :alt="agentName" loading="lazy" />
          </div>
          <h3>How can I help you today?</h3>
          <p>{{ welcomeText }}</p>
  
          <!-- Quick action suggestions -->
          <div class="quick-actions">
            <button
              v-for="(action, idx) in quickActions"
              :key="idx"
              class="quick-action-btn"
              @click="handleQuickAction(action.text)"
            >
              <span :class="['action-icon', 'icon-' + action.icon]"></span>
              <span>{{ action.text }}</span>
            </button>
          </div>
        </div>
  
        <!-- Message list -->
        <div
          v-for="(message, index) in messages"
          :key="index"
          :class="['message', message.role]"
          v-show="!message.isThinking && !message.isToolExecution"
        >
          <div class="message-content" :class="{ error: message.isError }">
            <!-- Attached files display for user messages -->
            <div v-if="message.attachedFiles && message.attachedFiles.length" class="attached-files-display">
              <div class="attached-files-label">
                <i class="el-icon-paperclip"></i>
                Attached files:
              </div>
              <div class="attached-files-list">
                <span
                  v-for="(fileName, fileIndex) in message.attachedFiles"
                  :key="fileIndex"
                  class="attached-file-tag"
                >
                  <i class="el-icon-document"></i>
                  {{ fileName }}
                </span>
              </div>
            </div>
            <!-- Render parsed content with cards -->
            <template v-for="(segment, segIndex) in parseContent(message.content)">
              <!-- Card segment -->
              <div
                v-if="segment.type === 'card'"
                :key="'card-' + segIndex"
                :class="['copilot-card', 'card-' + segment.cardType, { 'card-loading': segment.isStreaming }]"
              >
                <!-- Show loading indicator while card is streaming -->
                <div v-if="segment.isStreaming" class="card-loading-indicator">
                  <span class="thinking-spinner"></span>
                  <span>Generating...</span>
                </div>
                <!-- Show content once card is complete -->
                <template v-else>
                  <div class="card-content" v-html="renderMarkdown(segment.content)"></div>
                  <div v-if="segment.buttons && segment.buttons.length" class="card-actions">
                    <el-button
                      v-for="(btn, btnIndex) in segment.buttons"
                      :key="btnIndex"
                      :type="btn.action === 'cancel' ? 'default' : 'primary'"
                      size="small"
                      @click="handleCardAction(btn)"
                    >
                      {{ btn.label }}
                    </el-button>
                  </div>
                </template>
              </div>
              <!-- Markdown segment -->
              <div
                v-else
                :key="'md-' + segIndex"
                class="markdown-segment"
                v-html="renderMarkdown(segment.content)"
              ></div>
            </template>
          </div>
        </div>
  
        <!-- Floating tool status indicator (at bottom of messages) -->
        <div v-if="isThinking && !hasStreamingContent" class="tool-status-floating">
          <div class="tool-status-line">
            <span class="thinking-spinner"></span>
            <span class="tool-status-verb">{{ currentToolVerb }}</span>
            <button
              v-if="currentToolExecution && currentToolExecution.tools && currentToolExecution.tools.length"
              type="button"
              class="tool-details-toggle"
              @click="toolDetailsExpanded = !toolDetailsExpanded"
            >
              <span :class="['toggle-chevron', 'icon-chevron-down', { expanded: toolDetailsExpanded }]"></span>
              <span class="tool-count">{{ currentToolExecution.tools.length }} tool{{ currentToolExecution.tools.length > 1 ? 's' : '' }}</span>
            </button>
          </div>
          <!-- Expandable tool details -->
          <div
            v-if="toolDetailsExpanded && currentToolExecution && currentToolExecution.tools && currentToolExecution.tools.length"
            class="tool-details-expanded"
          >
            <span
              v-for="(tool, idx) in currentToolExecution.tools"
              :key="idx"
              class="tool-name-small"
            >
              {{ formatToolName(tool) }}
            </span>
          </div>
        </div>
      </div>
  
      <!-- Input Area -->
      <div class="input-area">
        <!-- File Preview -->
        <div v-if="selectedFiles.length > 0" class="file-preview-container">
          <div
            v-for="(file, index) in selectedFiles"
            :key="index"
            class="file-preview-chip"
          >
            <i :class="getFileIcon(file)" class="file-icon"></i>
            <span class="file-name">{{ file.name }}</span>
            <span class="file-size">({{ formatFileSize(file.size) }})</span>
            <button
              type="button"
              class="remove-file-btn"
              @click="removeFile(index)"
              :disabled="isThinking"
            >
              <i class="el-icon-close"></i>
            </button>
          </div>
        </div>
  
        <!-- Hidden file input -->
        <input
          type="file"
          ref="fileInput"
          @change="handleFileSelect"
          :accept="agentConfig.acceptedFileTypes"
          multiple
          style="display: none;"
        >
  
        <div class="input-row">
          <el-input
            v-model="userInput"
            type="textarea"
            :rows="2"
            :autosize="{ minRows: 1, maxRows: 4 }"
            placeholder="Type your message..."
            @keydown.enter.native="handleEnterKey"
            :disabled="isThinking"
            resize="none"
          />
  
          <div class="buttons-column">
            <!-- Icon buttons stacked vertically -->
            <div class="icon-buttons">
              <!-- Attachment button -->
              <button
                type="button"
                class="attach-button"
                @click="triggerFileInput"
                :disabled="isThinking"
                title="Attach files"
              >
                <span class="icon-paperclip"></span>
              </button>
  
              <!-- Voice input button -->
              <button
                type="button"
                class="voice-button"
                :class="{ 'is-recording': isRecording }"
                @click="toggleVoiceRecording"
                :disabled="isThinking || !speechRecognitionSupported"
                :title="voiceButtonTitle"
              >
                <span class="icon-microphone"></span>
              </button>
            </div>
  
            <el-button
              type="primary"
              :icon="isThinking ? '' : ''"
              @click="sendMessage"
              :disabled="!allowedToSendMessage"
              :loading="isThinking"
              class="send-button"
            >
              <span v-if="!isThinking">Send</span>
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </template>
<script>

import '@/assets/styles/agent-chat.css'
import '@/assets/styles/agent-chat-sidebar.css'
import BetaChip from '@shared_src/components/beta-chip.vue'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'AgentChat',
  components: {
    BetaChip,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    claimId: {
      type: String,
      default: '',
    },
    claimNumber: {
      type: String,
      default: '',
    },
    policyNumber: {
      type: String,
      default: '',
    },
  },

  data () {
    return {
      userInput: '',
      messages: [],
      isThinking: false,
      sessionId: null,
      activeEventSource: null,
      thinkingText: 'Thinking...',
      currentToolExecution: null,
      toolDetailsExpanded: false,
        // File attachment properties
      selectedFiles: [],
        // Voice recording properties
      isRecording: false,
      speechRecognition: null,
      speechRecognitionSupported: false,
    }
  },

  computed: {
    ...mapGetters('aiChat', ['agentConfig']),
    allowedToSendMessage () {
      return this.userInput.trim() && !this.isThinking
    },
    hasStreamingContent () {
      if (!this.messages || this.messages.length === 0) return false
      const lastMessage = this.messages[this.messages.length - 1]
      return (
          lastMessage &&
          lastMessage.role === 'assistant' &&
          lastMessage.content &&
          lastMessage.content.trim().length > 0
      )
    },

    agentName () {
      return this.agentConfig.name
    },

    agentIconPath () {
      return this.agentConfig.iconPath
    },

    welcomeText () {
      return this.agentConfig.welcomeText
    },

    quickActions () {
      return this.agentConfig.quickActions
    },

    voiceButtonTitle () {
      if (!this.speechRecognitionSupported) {
        return 'Voice input not supported in this browser'
      }
      return this.isRecording ? 'Click to stop recording' : 'Click to start voice input'
    },

    currentToolVerb () {
      if (
          !this.currentToolExecution ||
          !this.currentToolExecution.tools ||
          !this.currentToolExecution.tools.length
        ) {
        return 'Thinking...'
      }
        // Map tool names to user-friendly verbs
      const tools = this.currentToolExecution.tools
      const verbs = []
      for (let i = 0; i < tools.length; i++) {
        const tool = tools[i].toLowerCase()
        if (tool.includes('claim') && tool.includes('header')) {
          verbs.push('Retrieving claim header')
        } else if (tool.includes('claim') && tool.includes('info')) {
          verbs.push('Retrieving claim information')
        } else if (tool.includes('exposure')) {
          verbs.push('Listing exposures')
        } else if (tool.includes('coverage')) {
          verbs.push('Retrieving coverages')
        } else if (tool.includes('task')) {
          verbs.push('Retrieving tasks')
        } else if (tool.includes('note')) {
          verbs.push('Retrieving notes')
        } else if (tool.includes('attachment')) {
          verbs.push('Retrieving attachments')
        } else if (tool.includes('policy')) {
          verbs.push('Looking up policy')
        } else if (tool.includes('contact')) {
          verbs.push('Looking up contacts')
        } else if (tool.includes('reserve')) {
          verbs.push('Checking reserves')
        } else if (tool.includes('payment')) {
          verbs.push('Processing payment')
        }
      }
      return verbs.length > 0 ? verbs[0] + '...' : 'Working...'
    },
  },

  mounted () {
    this.initSpeechRecognition()
  },

  beforeDestroy () {
    this.closeConnection()
    this.stopRecording()
  },

  methods: {
    ...mapActions('aiChat', ['buildContext', 'createStreamingConnection', 'sendWithFiles']),
    onAgentChatClose () {
      this.$emit('on-close')
    },
    handleQuickAction (text) {
      this.userInput = text
      this.sendMessage()
    },

      // File attachment methods
    triggerFileInput () {
      this.$refs.fileInput.click()
    },

    handleFileSelect (event) {
      const files = Array.from(event.target.files || [])

      files.forEach(file => {
          // Check file size
        if (file.size > this.agentConfig.maxFileSize) {
          const maxSize = this.formatFileSize(this.agentConfig.maxFileSize)
          this.$message.warning(`File "${file.name}" exceeds the maximum size of ${maxSize}`)
          return
        }

          // Check for duplicates
        const isDuplicate = this.selectedFiles.some(f => f.name === file.name && f.size === file.size)
        if (isDuplicate) {
          this.$message.warning(`File "${file.name}" is already selected`)
          return
        }

        this.selectedFiles.push(file)
      })

      // Reset input so the same file can be selected again
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    removeFile (index) {
      this.selectedFiles.splice(index, 1)
    },

    getFileIcon (file) {
      const extension = file.name.split('.').pop().toLowerCase()
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
      const pdfExtensions = ['pdf']

      if (imageExtensions.includes(extension)) {
        return 'el-icon-picture-outline'
      } else if (pdfExtensions.includes(extension)) {
        return 'el-icon-document'
      } else {
        return 'el-icon-document'
      }
    },

    formatFileSize (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    handleEnterKey (event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        if (this.allowedToSendMessage) {
          this.sendMessage()
        }
      }
    },

    sendMessage () {
      if (!this.allowedToSendMessage) return

      const prompt = this.userInput.trim()

      // Build user message content with file info
      const attachedFileNames = this.selectedFiles.map(f => f.name)

      // Keep files for POST request before clearing
      const filesToSend = [...this.selectedFiles]

      // Add user message
      this.messages.push({
        role: 'user',
        content: prompt,
        attachedFiles: attachedFileNames.length > 0 ? attachedFileNames : null,
      })

      // Clear selected files
      this.selectedFiles = []

      // Clear input and set thinking state
      this.userInput = ''
      this.isThinking = true
      this.thinkingText = 'Thinking...'
      this.toolDetailsExpanded = false

      // Add placeholder for assistant response with all reactive properties
      const assistantMessage = {
        role: 'assistant',
        content: '',
        isThinking: true,
        isToolExecution: false,
        toolMessage: '',
        tools: [],
        isError: false,
      }
      this.messages.push(assistantMessage)

      this.$nextTick(() => this.scrollToBottom())

      // If files are attached, use POST with FormData
      if (filesToSend.length > 0) {
        this.sendMessageWithFiles(prompt, filesToSend, assistantMessage)
      } else {
          // Send via SSE streaming
        this.startStreamingChat(prompt, assistantMessage)
      }
    },

    async startStreamingChat (prompt, assistantMessage) {
      this.closeConnection()

      const context = await this.buildContext({
        claimId: this.claimId,
        claimNumber: this.claimNumber,
        policyNumber: this.policyNumber,
      })

      let streamingContent = ''
      this.activeEventSource = await this.createStreamingConnection({
        prompt,
        sessionId: this.sessionId,
        context,
        onContent: (content) => {
          // Content is arriving, hide thinking/tool indicators
          // Use $set to ensure Vue reactivity
          const msgIndex = this.messages.indexOf(assistantMessage)
          if (msgIndex !== -1) {
            this.$set(this.messages[msgIndex], 'isThinking', false)
            this.$set(this.messages[msgIndex], 'isToolExecution', false)
            streamingContent += content
            this.$set(this.messages[msgIndex], 'content', streamingContent)
          }
          this.currentToolExecution = null
          // Force scroll during streaming to follow content
          this.$nextTick(() => this.scrollToBottom(true))
        },
        onToolExecution: (data) => {
            // Tool execution in progress - update UI only if no content yet
          if (!streamingContent) {
            const msgIndex = this.messages.indexOf(assistantMessage)
            if (msgIndex !== -1) {
              this.$set(this.messages[msgIndex], 'isThinking', false)
              this.$set(this.messages[msgIndex], 'isToolExecution', true)
              this.$set(
                  this.messages[msgIndex],
                  'toolMessage',
                  data.content || 'Processing...'
                )
              this.$set(this.messages[msgIndex], 'tools', data.tools || [])
            }
            this.currentToolExecution = {
              message: data.content,
              tools: data.tools || [],
            }
          }
          this.thinkingText = data.content || 'Processing...'
          this.$nextTick(() => this.scrollToBottom())
        },
        onComplete: () => {
          this.closeConnection()
          const msgIndex = this.messages.indexOf(assistantMessage)
          if (msgIndex !== -1) {
            this.$set(this.messages[msgIndex], 'isThinking', false)
            this.$set(this.messages[msgIndex], 'isToolExecution', false)
          }
          this.currentToolExecution = null
          this.isThinking = false
          this.$nextTick(() => this.scrollToBottom())
        },
        onError: (error) => {
          this.handleError(error, assistantMessage)
        },
        onSessionId: (sessionId) => {
          if (!this.sessionId) {
            this.sessionId = sessionId
          }
        },
      })
    },
    async sendMessageWithFiles (prompt, files, assistantMessage) {
      try {
        const context = await this.buildContext({
          claimId: this.claimId,
          claimNumber: this.claimNumber,
          policyNumber: this.policyNumber,
        })
        const response = await this.sendWithFiles({
          prompt,
          sessionId: this.sessionId,
          context,
          files,
        })

        // Handle response - data is nested in response.data for API v2
        const msgIndex = this.messages.indexOf(assistantMessage)
        if (msgIndex !== -1) {
          this.$set(this.messages[msgIndex], 'isThinking', false)

          const data = response.data || response
          const sessionId = data.session_id || response.session_id

          if (sessionId && !this.sessionId) {
            this.sessionId = sessionId
          }

          if (response.success === false || response.error || data.error) {
              // Extract error message from various possible locations
            const errorMsg = response.error ||
              data.error ||
              (response.messages && response.messages.length > 0 ? response.messages.join('. ') : null) ||
              'An error occurred. Please try again.'
            this.handleError(errorMsg, assistantMessage)
          } else {
            this.$set(
                this.messages[msgIndex],
                'content',
                data.response || data.content || response.response || response.content || ''
              )
          }
        }

        this.isThinking = false
        this.$nextTick(() => this.scrollToBottom())
      } catch (error) {
        console.error('Error sending message with files:', error)
        this.handleError(
            error.message || 'Failed to send message with attachments',
            assistantMessage
          )
      }
    },

    handleError (errorMessage, assistantMessage) {
      this.closeConnection()

      // Remove the placeholder message
      const index = this.messages.indexOf(assistantMessage)
      if (index > -1) {
        this.messages.splice(index, 1)
      }

        // Add error message
      this.messages.push({
        role: 'assistant',
        content: `Error: ${errorMessage}`,
        isError: true,
      })

      this.isThinking = false
      this.$nextTick(() => this.scrollToBottom())
    },

    closeConnection () {
      this.$store.dispatch('aiChat/closeConnection', this.activeEventSource)
      this.activeEventSource = null
    },

    confirmClearConversation () {
      if (
          window.confirm(
            'Are you sure you want to clear the conversation? This will start a new session.'
          )
        ) {
        this.clearConversation()
      }
    },

    clearConversation () {
      this.closeConnection()
      this.messages = []
      this.sessionId = null
      this.userInput = ''
      this.isThinking = false
      this.currentToolExecution = null
      this.toolDetailsExpanded = false
      this.$emit('conversation-cleared')
    },

    scrollToBottom (force = false) {
      const container = this.$refs.messagesContainer
      if (container) {
          // Only auto-scroll if user is near the bottom (within 300px) or force is true
        const isNearBottom =
            container.scrollHeight - container.scrollTop - container.clientHeight <
            300
        if (force || isNearBottom) {
          container.scrollTop = container.scrollHeight
        }
      }
    },

      /**
       * Parse content into segments (cards and markdown)
       * Handles streaming by treating incomplete cards as still-loading
       */
    parseContent (content) {
      if (!content) return []

      const segments = []

      // Pattern to match complete cards: [card:type] ... [/card]
      const cardPattern = /\[card:(\w+)\]([\s\S]*?)\[\/card\]/g
      // Pattern to match button blocks: [buttons] ... [/buttons]
      const buttonPattern = /\[buttons\]([\s\S]*?)\[\/buttons\]/

      let lastIndex = 0
      let match

      while ((match = cardPattern.exec(content)) !== null) {
          // Add any markdown content before this card
        if (match.index > lastIndex) {
          const beforeContent = content.slice(lastIndex, match.index).trim()
          if (beforeContent) {
            segments.push({
              type: 'markdown',
              content: beforeContent,
            })
          }
        }

        const cardType = match[1]
        let cardContent = match[2]
        let buttons = null

        // Check for buttons inside the card
        const buttonMatch = buttonPattern.exec(cardContent)
        if (buttonMatch) {
            // Extract buttons and remove from card content
          buttons = this.parseButtons(buttonMatch[1])
          cardContent = cardContent.replace(buttonPattern, '').trim()
        }

        segments.push({
          type: 'card',
          cardType: cardType,
          content: cardContent.trim(),
          buttons: buttons,
        })

        lastIndex = match.index + match[0].length
      }

        // Add any remaining content after the last card
      if (lastIndex < content.length) {
        const afterContent = content.slice(lastIndex).trim()
        if (afterContent) {
            // Check if there's an incomplete card being streamed
          const incompleteCardMatch = afterContent.match(
              /\[card:(\w+)\]([\s\S]*)$/
            )
          if (incompleteCardMatch) {
              // There's content before the incomplete card
            const beforeIncomplete = afterContent
                .slice(0, incompleteCardMatch.index)
                .trim()
            if (beforeIncomplete) {
              segments.push({
                type: 'markdown',
                content: beforeIncomplete,
              })
            }
              // Show the incomplete card content (streaming)
            segments.push({
              type: 'card',
              cardType: incompleteCardMatch[1],
              content: incompleteCardMatch[2].trim(),
              buttons: null,
              isStreaming: true,
            })
          } else {
            segments.push({
              type: 'markdown',
              content: afterContent,
            })
          }
        }
      }

        // If no segments found, return the whole content as markdown
      if (segments.length === 0 && content.trim()) {
        segments.push({
          type: 'markdown',
          content: content,
        })
      }

      return segments
    },

      /**
       * Parse button definitions from button block
       * Format: [Button Label](action:action-name)
       */
    parseButtons (buttonContent) {
      const buttons = []
      const buttonPattern = /\[([^\]]+)\]\(action:([^)]+)\)/g
      let match

      while ((match = buttonPattern.exec(buttonContent)) !== null) {
        buttons.push({
          label: match[1],
          action: match[2],
        })
      }

      return buttons.length > 0 ? buttons : null
    },

      /**
       * Handle card action button clicks
       */
    handleCardAction (button) {
      this.userInput = button.label
      this.sendMessage()
    },

      /**
       * Render markdown to HTML with table support
       */
    renderMarkdown (content) {
      if (!content) return ''

      let html = content
          // Escape HTML first to prevent XSS
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')

      // Process tables first (before line breaks mess things up)
      html = this.renderTables(html)

      html = html
          // Code blocks (triple backticks)
          .replace(
            /```(\w*)\n([\s\S]*?)```/g,
            '<pre><code class="$1">$2</code></pre>'
          )
          // Inline code
          .replace(/`([^`]+)`/g, '<code>$1</code>')
          // Bold
          .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
          // Italic
          .replace(/\*([^*]+)\*/g, '<em>$1</em>')
          // Headers
          .replace(/^### (.+)$/gm, '<h4>$1</h4>')
          .replace(/^## (.+)$/gm, '<h3>$1</h3>')
          .replace(/^# (.+)$/gm, '<h2>$1</h2>')
          // Checkboxes (for checklists)
          .replace(
            /^\s*-\s*\[x\]\s+(.+)$/gm,
            '<li class="checklist-item checked"><span class="check-icon">✓</span><span>$1</span></li>'
          )
          .replace(
            /^\s*-\s*\[\s*\]\s+(.+)$/gm,
            '<li class="checklist-item"><span class="check-icon empty"></span><span>$1</span></li>'
          )
          // Bullet lists
          .replace(/^\s*[-*]\s+(.+)$/gm, '<li>$1</li>')
          // Numbered lists
          .replace(/^\s*\d+\.\s+(.+)$/gm, '<li>$1</li>')
          // Horizontal rules
          .replace(/^---$/gm, '<hr>')
          // Line breaks (but not inside tables or pre)
          .replace(/\n/g, '<br>')

      // Wrap consecutive <li> elements in <ul>
      html = html.replace(/(<li[^>]*>.*?<\/li>(<br>)?)+/g, (match) => {
        const hasChecklist = match.includes('checklist-item')
        const listClass = hasChecklist ? ' class="checklist"' : ''
        return '<ul' + listClass + '>' + match.replace(/<br>/g, '') + '</ul>'
      })

      // Clean up extra <br> after block elements
      html = html.replace(/<\/(h[234]|ul|table|pre|hr)><br>/g, '</$1>')
      html = html.replace(/<hr><br>/g, '<hr>')

      return html
    },

      /**
       * Render markdown tables to HTML tables
       */
    renderTables (content) {
      const lines = content.split('\n')
      const result = []
      let inTable = false
      let tableRows = []

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()

        // Check if this line looks like a table row
        if (line.startsWith('|') && line.endsWith('|')) {
          if (!inTable) {
            inTable = true
            tableRows = []
          }
          tableRows.push(line)
        } else {
            // End of table
          if (inTable) {
            result.push(this.buildTable(tableRows))
            inTable = false
            tableRows = []
          }
          result.push(line)
        }
      }

        // Handle table at end of content
      if (inTable && tableRows.length > 0) {
        result.push(this.buildTable(tableRows))
      }

      return result.join('\n')
    },

      /**
       * Build HTML table from markdown table rows
       */
    buildTable (rows) {
      if (rows.length < 2) return rows.join('\n')

      let html = '<div class="table-wrapper"><table>'

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]

        // Skip separator row (|---|---|)
        if (row.match(/^\|[\s\-:|]+\|$/)) continue

        const cells = row
            .split('|')
            .filter((cell, idx, arr) => idx !== 0 && idx !== arr.length - 1)
        const isHeader = i === 0
        const cellTag = isHeader ? 'th' : 'td'

        html += '<tr>'
        cells.forEach((cell) => {
          const cellContent = cell
              .trim()
              .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
          html += '<' + cellTag + '>' + cellContent + '</' + cellTag + '>'
        })
        html += '</tr>'
      }

      html += '</table></div>'
      return html
    },

      /**
       * Format tool name for display (convert snake_case to Title Case)
       */
    formatToolName (toolName) {
      if (!toolName) return ''
      return toolName
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (char) => char.toUpperCase())
    },

      // Speech recognition methods
    initSpeechRecognition () {
        // Check for browser support
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

      if (!SpeechRecognition) {
        this.speechRecognitionSupported = false
        console.warn('Speech recognition not supported in this browser')
        return
      }

      this.speechRecognitionSupported = true
      this.speechRecognition = new SpeechRecognition()

      // Configure recognition
      this.speechRecognition.continuous = true
      this.speechRecognition.interimResults = true
      this.speechRecognition.lang = 'en-US'

      // Event handlers
      this.speechRecognition.onresult = (event) => {
        this.handleSpeechResult(event)
      }
      this.speechRecognition.onerror = (event) => {
        this.handleSpeechError(event)
      }
      this.speechRecognition.onend = () => {
        this.handleSpeechEnd()
      }
    },

    toggleVoiceRecording () {
      if (!this.speechRecognitionSupported) {
        this.$message.warning('Voice input is not supported in this browser. Please use Chrome, Edge, or Safari.')
        return
      }

      if (this.isRecording) {
        this.stopRecording()
      } else {
        this.startRecording()
      }
    },

    startRecording () {
      if (!this.speechRecognition) {
        this.initSpeechRecognition()
      }

      if (!this.speechRecognitionSupported) {
        return
      }

      try {
        this.speechRecognition.start()
        this.isRecording = true
        this.$message.info('Listening... Click the microphone again to stop.')
      } catch (error) {
        console.error('Error starting speech recognition:', error)
        this.handleSpeechError({ error: 'start-failed' })
      }
    },

    stopRecording () {
      if (this.speechRecognition && this.isRecording) {
        try {
          this.speechRecognition.stop()
        } catch (error) {
          console.error('Error stopping speech recognition:', error)
        }
      }
      this.isRecording = false
    },

    handleSpeechResult (event) {
      let finalTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const transcript = result[0].transcript

        if (result.isFinal) {
          finalTranscript += transcript
        }
      }

      if (finalTranscript.trim()) {
          // Append transcribed text to userInput with a space separator
        const newText = finalTranscript.trim()
        if (this.userInput && this.userInput.trim()) {
          this.userInput = this.userInput.trim() + ' ' + newText
        } else {
          this.userInput = newText
        }
      }
    },

    handleSpeechError (event) {
      this.isRecording = false

      let errorMessage

      switch (event.error) {
      case 'not-allowed':
      case 'permission-denied':
        errorMessage = 'Microphone access denied. Please allow microphone access in your browser settings.'
        break
      case 'no-speech':
        errorMessage = 'No speech detected. Please try again.'
        break
      case 'audio-capture':
        errorMessage = 'No microphone found. Please ensure a microphone is connected.'
        break
      case 'network':
        errorMessage = 'Network error during voice recognition.'
        break
      case 'aborted':
            // User stopped recording, not an error
        return
      case 'start-failed':
        errorMessage = 'Failed to start voice recognition. Please try again.'
        break
      default:
        errorMessage = `Voice input error: ${event.error}`
      }

      this.$message.error(errorMessage)
    },

    handleSpeechEnd () {
      this.isRecording = false
    },
  },
}
</script>
